"use client";

import { createContext, useContext, useEffect, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import {
  SupportedCountries,
  SupportedLanguages,
} from "@/enums/internationalization";
import { THEME } from "@/enums/profile";
import { UserProfile } from "@/types/org";
import { GetUserProfile, UpdateUserProfile } from "@/utils/agencies/profiles";
import { DEFAULT_DASHBOARD_LAYOUT } from "@/utils/dashboard";
import {
  writeThemeCookie,
} from "@/utils/common/themeCookie";
import { useAlert } from "./useAlert";
import { useOrg } from "./useOrg";

type Colors = {
  primary: string;
  secondary: string;
  tertiary: string;
  primary_text: string;
  secondary_text: string;
  tertiary_text: string;
  panel_color: string;
  panel_secondary_color: string;
  text_color: string;
};

type UserProfileData = {
  colors: Colors;
  language: SupportedLanguages;
} & Omit<UserProfile, "language">;

type UserProfileProps = {
  userProfile: UserProfileData;
  saveUserProfile: (profile: Partial<UserProfile>) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  saveColumns: (key: string, columns: string[]) => Promise<void>;
  saveFavorites: (key: string, favorites: string[]) => Promise<void>;
};

const getDefaultColors = (theme: THEME = THEME.LIGHT): Colors => ({
  primary: "#3b82f6", // blue-500
  secondary: "#6b7280", // gray-500
  tertiary: "#10b981", // emerald-500
  primary_text: "#ffffff", // white
  secondary_text: "#ffffff", // white
  tertiary_text: "#ffffff", // white
  panel_color: "#f8fafc", // slate-50
  panel_secondary_color: "#f1f5f9", // slate-100
  text_color: theme === THEME.DARK ? "#dddddd" : "#333333", // adaptive text color
});

const colorsThemeFor = (theme: THEME): THEME.LIGHT | THEME.DARK =>
  theme === THEME.DARK ? THEME.DARK : THEME.LIGHT;

const getDefaultUserProfile = (): UserProfileData => {
  return {
    language: SupportedLanguages.English,
    favorites: {},
    columns: {},
    theme: THEME.LIGHT,
    dashboardLayout: JSON.stringify(DEFAULT_DASHBOARD_LAYOUT),
    colors: getDefaultColors(THEME.LIGHT),
    isDefaultProfile: true,
  };
};

/**
 * Persist the theme preference (cookie + localStorage) and notify the layout
 * to soft-apply it — no `location.reload()`.
 */
const applyThemePreference = (themePreference: THEME) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem("theme", themePreference);
  writeThemeCookie(themePreference);
  window.dispatchEvent(
    new CustomEvent("themeChanged", { detail: { theme: themePreference } })
  );
};

/**
 * Seed profile for SSR + first client paint from cookie-derived props so theme
 * Select / language Select match the server HTML (no Light→Dark flash).
 */
const createInitialUserProfile = (
  initialLanguage: SupportedLanguages,
  initialTheme: THEME
): UserProfileData => {
  const defaults: UserProfileData = {
    ...getDefaultUserProfile(),
    language: initialLanguage,
    theme: initialTheme,
    colors: getDefaultColors(colorsThemeFor(initialTheme)),
  };

  if (typeof window === "undefined") return defaults;
  try {
    const cached = window.localStorage.getItem("userProfile");
    if (!cached) return defaults;
    const parsed = JSON.parse(cached) as Partial<UserProfileData>;
    const { theme: _t, colors: _c, language: _l, ...rest } = parsed;
    return {
      ...defaults,
      ...rest,
      language: initialLanguage,
      theme: initialTheme,
      colors: getDefaultColors(colorsThemeFor(initialTheme)),
    };
  } catch {
    return defaults;
  }
};

const UserProfileContext = createContext<UserProfileProps>({
  userProfile: getDefaultUserProfile(),
  saveUserProfile: async () => {},
  refreshUserProfile: async () => {},
  saveColumns: async () => {},
  saveFavorites: async () => {},
});

const extractColorsFromCSS = (theme: THEME = THEME.LIGHT): Colors => {
  if (typeof window === "undefined") {
    return getDefaultColors(theme);
  }

  const getCSSVariable = (variable: string, defaultValue: string): string => {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(variable)
      .trim();
    return value || defaultValue;
  };

  const defaults = getDefaultColors(theme);

  return {
    primary: getCSSVariable("--gail-color-primary", defaults.primary),
    secondary: getCSSVariable("--gail-color-secondary", defaults.secondary),
    tertiary: getCSSVariable("--gail-color-tertiary", defaults.tertiary),
    primary_text: getCSSVariable(
      "--btn-primary-text-color",
      defaults.primary_text
    ),
    secondary_text: getCSSVariable(
      "--btn-secondary-text-color",
      defaults.secondary_text
    ),
    tertiary_text: getCSSVariable(
      "--btn-tertiary-text-color",
      defaults.tertiary_text
    ),
    panel_color: getCSSVariable("--panel-color", defaults.panel_color),
    panel_secondary_color: getCSSVariable(
      "--panel-secondary-color",
      defaults.panel_secondary_color
    ),
    text_color: defaults.text_color, // Always use theme-based default for text_color
  };
};

const areColorsEqual = (left: Colors, right: Colors): boolean =>
  left.primary === right.primary &&
  left.secondary === right.secondary &&
  left.tertiary === right.tertiary &&
  left.primary_text === right.primary_text &&
  left.secondary_text === right.secondary_text &&
  left.tertiary_text === right.tertiary_text &&
  left.panel_color === right.panel_color &&
  left.panel_secondary_color === right.panel_secondary_color &&
  left.text_color === right.text_color;

export const useUserProfile = () => useContext(UserProfileContext)!;

const useUserProfileFunction = ({
  setAppLanguage,
  initialLanguage,
  initialTheme,
}: {
  setAppLanguage: (language: string) => void;
  initialLanguage: SupportedLanguages;
  initialTheme: THEME;
}): UserProfileProps => {
  const { user } = useAuthContext();
  const { org } = useOrg();
  const { setIsSaving, setAlert } = useAlert();
  const { t: ct } = useTranslation("common");

  const [userProfile, setUserProfile] = useState<UserProfileData>(() =>
    createInitialUserProfile(initialLanguage, initialTheme)
  );

  const fetchResources = async () => {
    if (!user?.id) return;
    // API calls use cookies automatically via credentials: 'include'
    const newProfile = await GetUserProfile(user.id);

    const existingTheme =
      window.localStorage.getItem("theme") || initialTheme || THEME.LIGHT;

    if (newProfile.theme !== existingTheme) {
      applyThemePreference(newProfile.theme);
    }

    const orgLanguage =
      (org.localization?.split("-")[0] as SupportedLanguages) ||
      SupportedLanguages.English;
    const resolvedLanguage = newProfile.language || orgLanguage;

    setUserProfile((prev) => ({
      ...prev,
      ...newProfile,
      language: resolvedLanguage,
      isDefaultProfile: newProfile.isDefaultProfile ?? false,
      layout: newProfile.dashboardLayout
        ? newProfile.dashboardLayout
        : DEFAULT_DASHBOARD_LAYOUT,
      colors: extractColorsFromCSS(newProfile.theme),
    }));
  };

  useEffect(() => {
    if (user && user.agencyId) {
      fetchResources();
    } else {
      setUserProfile(getDefaultUserProfile());
    }
  }, [user?.agencyId]);

  // Effect to detect CSS variable changes and update colors
  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateColors = () => {
      setUserProfile((prev) => {
        const nextColors = extractColorsFromCSS(prev.theme);
        if (areColorsEqual(prev.colors, nextColors)) {
          return prev;
        }

        return {
          ...prev,
          colors: nextColors,
        };
      });
    };

    // Update colors when brand changes (which might affect CSS variables)
    updateColors();

    // Set up a MutationObserver to watch for style changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style" &&
          mutation.target === document.documentElement
        ) {
          updateColors();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });

    // Also listen for custom events that might indicate brand/theme changes
    const handleBrandChange = () => updateColors();
    window.addEventListener("brandChanged", handleBrandChange);
    window.addEventListener("themeChanged", handleBrandChange);

    return () => {
      observer.disconnect();
      window.removeEventListener("brandChanged", handleBrandChange);
      window.removeEventListener("themeChanged", handleBrandChange);
    };
  }, [userProfile.theme, org.details?.brand]);

  useEffect(() => {
    // After mount, merge cached non-preference fields. Theme + language stay
    // cookie/prop-backed so SSR and first paint already showed the right values.
    const cachedProfile = window.localStorage.getItem("userProfile");
    if (cachedProfile) {
      try {
        const parsed = JSON.parse(cachedProfile) as Partial<UserProfileData>;
        const { theme: _t, language: _l, colors: _c, ...rest } = parsed;
        setUserProfile((prev) => ({
          ...prev,
          ...rest,
          language: prev.language,
          theme: prev.theme,
          colors: extractColorsFromCSS(prev.theme),
        }));
      } catch {
        setUserProfile((prev) => ({
          ...prev,
          colors: extractColorsFromCSS(prev.theme),
        }));
      }
      return;
    }

    let locale =
      navigator.languages && navigator.languages.length > 0
        ? navigator.languages[0]
        : "en-US";

    let country =
      locale.split("-").length > 1
        ? (locale.split("-")[1] as SupportedCountries)
        : SupportedCountries.UnitedStates;

    let language =
      locale.split("-").length > 1
        ? (locale.split("-")[0] as SupportedLanguages)
        : SupportedLanguages.English;

    if (
      !Object.values(SupportedLanguages).find((l) => l === locale.split("-")[0])
    ) {
      locale = "en-US";
    }

    if (!Object.values(SupportedCountries).find((l) => l === country)) {
      country = SupportedCountries.UnitedStates;
    }

    setAppLanguage(language);

    setUserProfile((prev) => ({
      ...prev,
      language,
      colors: extractColorsFromCSS(prev.theme),
    }));
  }, []);

  useEffect(() => {
    window.localStorage.setItem("userProfile", JSON.stringify(userProfile));
  }, [userProfile]);

  useEffect(() => {
    if (userProfile.isDefaultProfile) {
      const orgLanguage =
        (org.localization?.split("-")[0] as SupportedLanguages) ||
        SupportedLanguages.English;
      setAppLanguage(orgLanguage);
      if (userProfile.language !== orgLanguage) {
        setUserProfile((prev) => ({ ...prev, language: orgLanguage }));
      }
      return;
    }

    setAppLanguage(userProfile.language);
  }, [userProfile, org.localization]);

  const saveUserProfile = async (
    profile: Partial<UserProfile>,
    isSilent: boolean = false
  ) => {
    if (!user?.id) return;

    if (!isSilent) {
      setIsSaving(true, ct("savingUserSettings"));
    }

    // API calls use cookies automatically via credentials: 'include'
    const newProfile = await UpdateUserProfile(user.id, profile);

    if (!newProfile) {
      if (!isSilent) {
        setIsSaving(false, "");
        setAlert({
          status: "error",
          message: ct("failedUserSettings"),
          secondsToClose: 10,
        });
      }

      return;
    }

    const updatedProfile: UserProfile & { language: SupportedLanguages } = {
      ...newProfile,
      theme: newProfile.theme || userProfile.theme,
      dashboardLayout:
        newProfile.dashboardLayout || userProfile.dashboardLayout,
      favorites: newProfile.favorites ?? userProfile.favorites,
      columns: newProfile.columns ?? userProfile.columns,
      language: newProfile.language || userProfile.language,
      isDefaultProfile: false,
    };

    if (!isSilent) {
      setIsSaving(false, "");
    }

    const existingTheme = window.localStorage.getItem("theme");

    if (updatedProfile.language !== userProfile.language) {
      setAppLanguage(updatedProfile.language);
    }

    if (updatedProfile.theme !== existingTheme) {
      applyThemePreference(updatedProfile.theme);
    }

    if (!isSilent) {
      setAlert({
        status: "success",
        message: ct("savedUserSettings"),
        secondsToClose: 4,
      });
    }

    setUserProfile((prev) => ({
      ...prev,
      ...updatedProfile,
      layout: JSON.parse(updatedProfile.dashboardLayout),
      colors: extractColorsFromCSS(updatedProfile.theme),
    }));
  };

  const saveColumns = async (key: string, columns: string[]) => {
    await saveUserProfile(
      {
        ...(userProfile.isDefaultProfile ? userProfile : {}),
        columns: { ...userProfile.columns, [key]: columns },
      },
      true
    );
    setUserProfile((prev) => ({
      ...prev,
      columns: { ...prev.columns, [key]: columns },
    }));
  };

  const saveFavorites = async (key: string, favorites: string[]) => {
    await saveUserProfile(
      {
        ...(userProfile.isDefaultProfile ? userProfile : {}),
        favorites: { ...userProfile.favorites, [key]: favorites },
      },
      true
    );
    setUserProfile((prev) => ({
      ...prev,
      favorites: { ...prev.favorites, [key]: favorites },
    }));
  };

  return {
    userProfile,
    saveUserProfile,
    refreshUserProfile: fetchResources,
    saveColumns,
    saveFavorites,
  };
};

export const UserProfileContextProvider = ({
  children,
  setLanguage,
  initialLanguage = SupportedLanguages.English,
  initialTheme = THEME.LIGHT,
}: {
  children: React.ReactNode;
  setLanguage: (language: string) => void;
  initialLanguage?: SupportedLanguages;
  initialTheme?: THEME;
}) => {
  const profileFunction = useUserProfileFunction({
    setAppLanguage: setLanguage,
    initialLanguage,
    initialTheme,
  });

  return (
    <UserProfileContext.Provider value={profileFunction}>
      {children}
    </UserProfileContext.Provider>
  );
};
